import { useEffect, useRef, type RefObject } from "react";
import type { FuseAuthProviderState } from "@fuse/core/FuseAuthProvider/types/FuseAuthTypes";
import type { User } from "@auth/user";
import { useRefreshTokenAuthSpace } from "@/api/space/hooks/useSpace.authenticator";
import { aplicarSessaoAutenticadaSpaceApi, getGrupoAtualAutenticacaoSpaceApi } from "@/api/space/rootAuth";
import {
	estaDoisFatoresPendente,
	foiLogoutExplicito,
	limparLogoutExplicito,
} from "@/api/space/estadoAutenticacao.space";
import { mapearRespostaLoginParaUsuario } from "../utils/mapearUsuarioAutenticado";

// --------------------------------- TYPES ---------------------------------
type SessaoRestaurada = {
	readonly user: User;
	readonly token: string;
	readonly groupId: string;
};

type UseRestauracaoSessaoJwtOptions = {
	readonly status: FuseAuthProviderState<User>["authStatus"];
	readonly versaoSessaoRef: RefObject<number>;
	readonly montadoRef: RefObject<boolean>;
	readonly aoAutenticar: (user: User) => void;
	readonly aoDesautenticar: () => void;
};

// ------------------------------- FUNCTIONS -------------------------------
export default function useRestauracaoSessaoJwt({
	status,
	versaoSessaoRef,
	montadoRef,
	aoAutenticar,
	aoDesautenticar,
}: Readonly<UseRestauracaoSessaoJwtOptions>): void {
	// -------------------------------- STATES --------------------------------
	const restauracaoRef = useRef<Promise<SessaoRestaurada | null> | null>(null);

	// -------------------------------- QUERIES -------------------------------
	const { mutateAsync: refreshToken } = useRefreshTokenAuthSpace();

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		if (status !== "configuring") return;
		let cancelado = false;

		const versaoSessao = versaoSessaoRef.current;
		const groupId = getGrupoAtualAutenticacaoSpaceApi();
		const sessaoAtual = () => montadoRef.current && versaoSessaoRef.current === versaoSessao;

		async function tentarLoginAutomatico(): Promise<SessaoRestaurada | null> {
			if (estaDoisFatoresPendente() || foiLogoutExplicito() || !groupId) return null;
			try {
				const resposta = await refreshToken(groupId);
				if (!resposta?.token?.trim() || !sessaoAtual()) return null;
				return {
					user: mapearRespostaLoginParaUsuario(resposta, { groupIdPadrao: groupId }),
					token: resposta.token,
					groupId,
				};
			} catch {
				return null;
			}
		}

		restauracaoRef.current ??= tentarLoginAutomatico();
		void restauracaoRef.current.then((sessao) => {
			if (cancelado || !sessaoAtual()) return;
			if (!sessao) return aoDesautenticar();
			aplicarSessaoAutenticadaSpaceApi({ token: sessao.token, groupId: sessao.groupId });
			limparLogoutExplicito();
			aoAutenticar(sessao.user);
		});

		return () => {
			cancelado = true;
		};
	}, [status, refreshToken, versaoSessaoRef, montadoRef, aoAutenticar, aoDesautenticar]);
}
